import createClient from "openapi-fetch";
import type { paths } from "../types/generated/api-schema";
import allStores from "../stores/StoreLoader";
import {
  ExecutionEnvironment,
  isServerComponent,
} from "../stores/InterfaceStore";
import { getCookieFromString } from "../utils/StringUtilities";
import { getApiBaseUrl } from "../utils/apiBaseUrl";

import { unwrapClient, type UnwrappedClient } from "./ApiClientHelpers";

export { ApiError, toFormData } from "./ApiClientHelpers";

const client = createClient<paths>({
  baseUrl: getApiBaseUrl(),
  // the backend's list filters (id__in, type__in, etc.) expect comma-separated
  // values ("multiple values may be separated by commas" per the spec's docs),
  // not openapi-fetch's default repeated-key style (SBSB-2912)
  querySerializer: { array: { style: "form", explode: false } },
});

client.use({
  async onRequest({ request }) {
    let token: string | null = null;
    if (ExecutionEnvironment.canUseDOM) {
      token = getCookieFromString("token", document.cookie);
    } else if (isServerComponent) {
      const { cookies } = await import("next/headers");
      const c = await cookies();
      token = c.get("token")?.value || null;
    }

    if (token) {
      request.headers.set("api-key", token);
    }
    request.headers.set(
      "accept-language",
      allStores.i18nStore.currentOrgSupportedLocale,
    );
    request.headers.set("app-ctx", process.env.NEXT_PUBLIC_APP_CTX ?? "");

    return request;
  },
});

// wrapped so every method returns Promise<T> directly (throwing ApiError on
// failure) instead of Promise<{data, error, response}> - no per-call unwrap().
// generated `paths` has no index signature, so it can't satisfy
// UnwrappedClient's constraint directly - see the note on UnwrappedClient in
// ApiClientHelpers.ts for why this is suppressed once here, rather than
// routed around with a conditional type.
// @ts-expect-error - paths lacks an index signature, see note above
type ApiClientType = UnwrappedClient<paths>;

// @ts-expect-error - paths lacks an index signature, see note above
const unwrappedClient: ApiClientType = unwrapClient(client);

export default function ApiClient(): Promise<ApiClientType> {
  return Promise.resolve(unwrappedClient);
}
